import React, { useCallback, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './BasicNotes.css';

const NOTE_FREQUENCIES = [
  { name: 'Low G', frequency: 426 },
  { name: 'Low A', frequency: 480 },
  { name: 'B', frequency: 540 },
  { name: 'C', frequency: 600 },
  { name: 'D', frequency: 640 },
  { name: 'E', frequency: 720 },
  { name: 'F', frequency: 800 },
  { name: 'High G', frequency: 840 },
  { name: 'High A', frequency: 960 },
];

function BasicNotes({ playTone }) {
  const [simulateChanter, setSimulateChanter] = useState(false);
  const [includeDrones, setIncludeDrones] = useState(false);
  const [continuousPlay, setContinuousPlay] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [droneVolume, setDroneVolume] = useState(0.5); // Default drone volume at 50%
  const cleanupRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleNoteClick = useCallback(
    frequency => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // In continuous mode, if clicking the same note that's playing, stop it
      if (continuousPlay && currentlyPlaying === frequency) {
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
        }
        setCurrentlyPlaying(null);
        return;
      }

      // Stop any currently playing note before starting a new one
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      // Start playing the new note
      if (typeof window !== 'undefined' && window.AudioContext) {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const cleanup = playTone(frequency, audioContext, {
          simulateChanter,
          includeDrones,
          droneVolume,
          continuousPlay,
        });

        cleanupRef.current = cleanup;
        setCurrentlyPlaying(frequency);

        // In non-continuous mode, stop after 1 second
        if (!continuousPlay) {
          timeoutRef.current = setTimeout(() => {
            if (cleanupRef.current) {
              cleanupRef.current();
              cleanupRef.current = null;
            }
            setCurrentlyPlaying(null);
          }, 1000);
        }
      }
    },
    [playTone, simulateChanter, includeDrones, continuousPlay, currentlyPlaying, droneVolume]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  return (
    <div className="app-theme basic-notes">
      <div className="basic-notes-container">
        <h2>Basic Notes</h2>
        <p>
          Click any note to hear how it sounds on the Great Highland Bagpipe. The scale starts at Low
          G and goes up to High A.
        </p>
        <div className="sound-options">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={simulateChanter}
              onChange={e => setSimulateChanter(e.target.checked)}
            />
            Simulate Chanter
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeDrones}
              onChange={e => setIncludeDrones(e.target.checked)}
            />
            Include Drones
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={continuousPlay}
              onChange={e => {
                setContinuousPlay(e.target.checked);
                // If turning off continuous play, clean up any playing note
                if (!e.target.checked && cleanupRef.current) {
                  cleanupRef.current();
                  cleanupRef.current = null;
                  setCurrentlyPlaying(null);
                }
              }}
            />
            Continuous Play
          </label>
          {includeDrones && (
            <div className="volume-control">
              <label htmlFor="drone-volume" className="volume-label">
                Drone Volume
                <div className="volume-value">{Math.round(droneVolume * 100)}%</div>
              </label>
              <input
                type="range"
                id="drone-volume"
                min="0"
                max="1"
                step="0.01"
                value={droneVolume}
                onChange={e => {
                  const newVolume = parseFloat(e.target.value);
                  setDroneVolume(newVolume);
                  // If a note is currently playing, update its volume
                  if (cleanupRef.current && currentlyPlaying) {
                    handleNoteClick(currentlyPlaying);
                  }
                }}
                className="volume-slider"
              />
            </div>
          )}
        </div>
        <div className="button-container">
          {NOTE_FREQUENCIES.map(({ name, frequency }) => (
            <button
              key={name}
              type="button"
              onClick={() => handleNoteClick(frequency)}
              aria-label={`Play ${name} note`}
              className={currentlyPlaying === frequency ? 'playing' : ''}
            >
              {name}
            </button>
          ))}
        </div>
        <p className="note-disclaimer">
          Note: These are synthesized tones approximating the actual bagpipe notes.
        </p>
      </div>
    </div>
  );
}

BasicNotes.propTypes = {
  playTone: PropTypes.func.isRequired,
};

export default BasicNotes;
